import type { GamePageTranslations } from "@casino/server/graphql/getCmsGamePagePhrases";
import translatePhrase from "@casino/ui/Games/translatePhrase";
import { cn, TypographyText } from "@workspace/frontend-ui";
import type { FC } from "react";
import {
  RANK_BADGE_IMAGES,
  RANK_ICON_IMAGES,
  siteArtwork,
} from "./tournamentRankImages";
import truncatePointsToTwoDecimals from "./truncatePointsToTwoDecimals";

type Props = {
  position?: number;
  name?: string;
  points?: string;
  translations?: GamePageTranslations;
  site: Site;
};

function TournamentIcon({ rank, site }: { rank?: number; site: Site }) {
  const images =
    rank === 1 || rank === 2 || rank === 3
      ? RANK_ICON_IMAGES[rank]
      : RANK_ICON_IMAGES.default;

  return (
    <img
      src={siteArtwork(images, site)}
      className={cn("w-10", site === "sportsbet" && "transform -translate-y-2")}
      alt=""
    />
  );
}

const MyTournamentStanding: FC<Props> = ({
  position,
  name,
  points,
  translations,
  site,
}) => {
  if (position === undefined) {
    return null;
  }

  const displayPoints =
    points && !Number.isNaN(Number(points))
      ? truncatePointsToTwoDecimals(points)
      : "-";

  return (
    <div
      data-testid="tournament-my-standing"
      className="flex flex-nowrap items-center gap-x-2 w-fit"
    >
      <div
        className="size-13 bg-contain rounded-md relative flex justify-center items-center"
        style={{
          backgroundImage: `url(${siteArtwork(RANK_BADGE_IMAGES, site)})`,
        }}
      >
        <TournamentIcon rank={position} site={site} />
        <TypographyText
          className="text-white! font-semibold lowercase flex flex-wrap absolute text-center justify-center bottom-px left-0 w-full"
          variant="small"
          as="p"
        >
          <span className="sr-only">
            {translatePhrase(
              translations,
              "game.tournament.position",
              "Position",
            )}
          </span>
          <TypographyText
            as="span"
            variant="small"
            className="text-xs m-0! p-0! leading-none!"
          >
            {`#${position}`}
          </TypographyText>
        </TypographyText>
      </div>
      <div className="flex flex-col justify-center h-full">
        <TypographyText
          as="p"
          variant="small"
          className="text-white! font-semibold opacity-80"
        >
          <span className="sr-only">
            {translatePhrase(
              translations,
              "game.tournament.username",
              "Username",
            )}
          </span>
          {name && <span>{name}</span>}
        </TypographyText>
        <TypographyText
          as="p"
          variant="small"
          className="text-white! font-semibold mt-0!"
        >
          <span>{displayPoints}</span>{" "}
          <span>
            {translatePhrase(translations, "game.tournament.points", "Points")}
          </span>
        </TypographyText>
      </div>
    </div>
  );
};

export default MyTournamentStanding;
